import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';
import {ticketMutationAdapter} from '../../../adapter/ticketMutationAdapter';
import {AGE_GRADE_TO_NUMBER_MAP} from '../../../common/constant';
import {t, tt} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {API_STATUS_MESSAGE_MAP} from '../../../common/shared/api/nicolas/ApiStatusMessageMap';
import {ApiStatusCodeVo} from '../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import useTypedSelector from '../../../common/useTypedSelector';
import {kakaoLogin} from '../../../domain/login/util/kakaoLogin';
import {getSerializedPagewebError} from '../../../errors/getSerializedPagewebError.function';
import {handleRestrictedCountryDeniedError} from '../../../errors/handleRestrictionCountryDeniedError';
import {PagewebErrorType} from '../../../errors/PagewebErrorType.enum';
import {FreeTicketType, TicketKind} from '../../../graphql/generated/types';
import {AGE_GRADE_TO_SELF_CERT_REQUEST_TYPE_MAP} from '../../../selfCert/constants';
import {showToast} from '../../../toast/toastHandler';
import {processSelfCertRequest} from '../../../user/processSelfCertRequest';
import {useTodayGiftListQuery} from '../../../wrapper';
import {ReceiveTicketInfo} from '../type';
import {setTodayGiftListQueryData} from '../util/setTodayGiftListQueryData';

export function useClickGiftButton({
  categoryUid,
  subCategoryUid,
}: {
  categoryUid: string;
  subCategoryUid?: string;
}): {
  onClickGiftButton: ({
    ticketUid,
    title,
    seriesId,
    ageGrade,
    isHelixGift,
  }: ReceiveTicketInfo) => void;
} {
  const queryClient = useQueryClient();
  const {user} = useTypedSelector(state => state.user);
  const {mutateAsync} = useMutation(ticketMutationAdapter.receiveTicketFree());
  const {isMobile} = requestContext.requestInfo.display;

  const onClickGiftButton = useCallback(
    async ({
      ticketUid,
      title,
      seriesId,
      ageGrade,
      isHelixGift,
    }: ReceiveTicketInfo) => {
      const params = [];

      if (subCategoryUid) {
        params.push(`subcategory_uid=${subCategoryUid}`);
      }

      if (ticketUid) {
        params.push(`ticket_uid=${ticketUid}`);
        if (title) {
          params.push(`title=${encodeURIComponent(title)}`);
        }
        if (ageGrade) {
          params.push(`age_grade=${ageGrade}`);
        }
        if (seriesId) {
          params.push(`series_id=${seriesId}`);
        }
        if (isHelixGift) {
          params.push(`is_helix_gift=${isHelixGift}`);
        }
      }

      const returnUrlParams = params.length > 0 ? `?${params.join('&')}` : '';
      const returnUrl = `/today-gift/${categoryUid}${returnUrlParams}`;

      async function onLoggedIn() {
        try {
          if ((ticketUid !== 0 && !ticketUid) || !user) {
            throw new Error();
          }

          if (ageGrade) {
            const ageGradeNum = AGE_GRADE_TO_NUMBER_MAP[ageGrade];
            if (ageGradeNum >= 19) {
              // 미인증 유저인 경우 19세 이상 작품 선물받기 시도 시 본인인증을 시도합니다.
              if (!user.age) {
                processSelfCertRequest({
                  selfCertRequestParams: {
                    type: AGE_GRADE_TO_SELF_CERT_REQUEST_TYPE_MAP[ageGrade],
                    success_url: returnUrl,
                    redirect_in_window: false,
                  },
                });
                return;
              }
              // 이미 인증된 유저이지만 19세 미만 유저인 경우 오류메시지 토스트를 노출합니다.
              else if (user.age < ageGradeNum) {
                const toastText = t('giftbox_get_gift_age_limit_toast', {
                  values: {
                    value: ageGradeNum,
                  },
                });
                showToast({message: toastText, isMobile});
                return;
              }
            }
          }

          const {receiveTicketFree} = await mutateAsync({
            input: {
              ticketUid,
              seriesId,
              type: isHelixGift
                ? FreeTicketType.HelixGift
                : FreeTicketType.TodayGift,
            },
          });

          if (!receiveTicketFree || !receiveTicketFree.isReceived) {
            throw new Error();
          }

          const toastText = t('giftbox_toast_ticket', {
            values: {
              value: title || '',
              gift_ticket_count: t(
                receiveTicketFree.ticketKind === TicketKind.Own
                  ? 'common_ticket_possession_amount'
                  : 'common_ticket_rental_amount',
                {values: {value: receiveTicketFree.ticketCount}}
              ),
            },
          });
          showToast({message: toastText, isMobile});

          queryClient.setQueryData(
            useTodayGiftListQuery.getKey({categoryUid, subCategoryUid}),
            (prev: any) => {
              const data = setTodayGiftListQueryData(
                prev,
                receiveTicketFree,
                ticketUid
              );
              return data;
            }
          );
        } catch (error) {
          const serializedPagewebError = getSerializedPagewebError(error);

          if (
            serializedPagewebError.type === PagewebErrorType.NetworkStatusError
          ) {
            showToast({
              message: t('error_network_detail'),
              isMobile,
            });
            return;
          }

          if (
            serializedPagewebError.resultCode ===
            ApiStatusCodeVo.ItemAccessRestrictionCountryDenied
          ) {
            handleRestrictedCountryDeniedError(serializedPagewebError);
          } else {
            showToast({
              message:
                tt(
                  API_STATUS_MESSAGE_MAP[
                    serializedPagewebError.resultCode as number
                  ]
                ) ?? t('error_api_toast'),
              isMobile,
            });
            return;
          }
        }
      }

      async function onLoggedOut() {
        await kakaoLogin({
          callbackUrl: returnUrl,
          relayPagePath: '/relay/login',
        });
      }

      if (user) {
        await onLoggedIn();
      } else {
        await onLoggedOut();
      }
    },
    [subCategoryUid, categoryUid, user, mutateAsync, isMobile, queryClient]
  );

  return {
    onClickGiftButton,
  };
}
